/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { SessionErrorResponseCodeEnum } from 'client-code/session-manager';

import { Session } from '../../entities/Session';
import { LiveTvError } from '../../error';
import { ServicesClientImpl } from '../../interactors';
import { IServices } from '../../interfaces/IServices';
import { Log } from '../../logging';
import { TSessionState } from '../../types';
import { UniqueIdentifier } from '../../utils';
import { PlaybackErrorType, SessionState } from '../enums';
import { LiveTvPlaybackErrorMapper, LiveTvSessionErrorMapper } from '../error';
import { IPlaybackError, ISessionCreateModel, IStreamingSession } from '../interfaces';

export abstract class StreamingSessionImpl implements IStreamingSession {
    protected TAG: string = 'StreamingSessionImpl';

    protected mSessionState: SessionState = SessionState.NONE;

    protected mServices: IServices = new ServicesClientImpl();

    protected oSession = {} as Session;

    protected mError: IPlaybackError = {} as IPlaybackError;

    protected timeoutId: any;

    protected SESSION_MANAGER_RETRY_ON_ERROR_MESSAGE = [
        SessionErrorResponseCodeEnum.ExternalError,
        SessionErrorResponseCodeEnum.HostUnavailable,
        SessionErrorResponseCodeEnum.InternalError,
        SessionErrorResponseCodeEnum.NoSession,
        SessionErrorResponseCodeEnum.UnknownLocation,
    ];

    protected SESSION_MANAGER_DEFAULT_RETRY_OPTIONS = {
        retryOn: {
            '400': {
                delayType: 'incremental',
                delay: [2000, 3000, 5000, 7000, 11000, 13000],
                errorMessage: this.SESSION_MANAGER_RETRY_ON_ERROR_MESSAGE,
            },
        },
    };

    public getPlaybackError = (error: string) => {
        const playbackError: LiveTvError = new LiveTvPlaybackErrorMapper().getErrorByCode(error);
        Diagnostics.debug(this.TAG, `handlePlaybackError {playbackError: `, playbackError, '}');
        Log.d(this.TAG, `handlePlaybackError {playbackError: `, playbackError, '}');
        return {
            type: PlaybackErrorType.SESSION_ERROR,
            data: playbackError,
        };
    };

    protected getSessionError = (error: any, isNdvrPlayback?: boolean) => {
        let sessionError: LiveTvError = {} as LiveTvError;
        if (error && error.response && error.response.data) {
            sessionError = new LiveTvSessionErrorMapper().getErrorByErrorObject(
                error.response.data,
                isNdvrPlayback,
            );

            Diagnostics.info(this.TAG, `handleSessionError`, JSON.stringify(sessionError));
            Log.i(this.TAG, `handleSessionError`, JSON.stringify(sessionError));
        }
        return {
            type: PlaybackErrorType.PLAYBACK_ERROR,
            data: sessionError,
        };
    };

    // eslint-disable-next-line no-unused-vars
    abstract createStreamingSession(sessionCreateModel: ISessionCreateModel): Promise<void>;

    public sessionKeepAlive(): Promise<void> {
        const { sessionId } = this.oSession;
        return new Promise((resolve, reject) => {
            if (sessionId) {
                this.mServices
                    .sessionKeepAlive(this.oSession, UniqueIdentifier.getNextSessionNetworkCallId())
                    .then((response: TSessionState) => {
                        if (
                            response.sessionNetworkCallId ===
                            UniqueIdentifier.getSessionNetworkCallId()
                        ) {
                            Diagnostics.debug(
                                this.TAG,
                                `{sessionKeepAlive: ${JSON.stringify(response)}}`,
                            );
                            Log.d(this.TAG, `{sessionKeepAlive: ${JSON.stringify(response)}}`);
                            resolve();
                        }
                    })
                    .catch((error) => {
                        if (
                            error.sessionNetworkCallId ===
                            UniqueIdentifier.getSessionNetworkCallId()
                        ) {
                            if (error.response) this.mError = this.getSessionError(error);
                            Diagnostics.error(this.TAG, `sessionKeepAlive:${error}`);
                            Log.e(this.TAG, `sessionKeepAlive:${error}`);
                            reject();
                        }
                    });
            } else {
                reject();
            }
        });
    }

    public sessionStop(): void {
        const { sessionId } = this.oSession;
        if (sessionId) {
            clearTimeout(this.timeoutId);
            clearInterval(this.timeoutId);
            this.mServices
                .sessionDelete(this.oSession, UniqueIdentifier.getNextSessionNetworkCallId())
                .then((response: TSessionState) => {
                    if (
                        response.sessionNetworkCallId === UniqueIdentifier.getSessionNetworkCallId()
                    ) {
                        Diagnostics.debug(this.TAG, `sessionStop${JSON.stringify(response)}`);
                        Log.d(this.TAG, `sessionStop${JSON.stringify(response)}`);
                        this.oSession = {} as Session;
                    }
                })
                .catch((error) => {
                    if (error.sessionNetworkCallId === UniqueIdentifier.getSessionNetworkCallId()) {
                        Diagnostics.error(this.TAG, `sessionStop:${error}`);
                        Log.e(this.TAG, `sessionStop:${error}`);
                    }
                });
        }
    }

    public getSessionState(): SessionState {
        return this.mSessionState;
    }

    public setSessionState(state: SessionState): void {
        this.mSessionState = state;
    }

    public getSessionPlaybackError(): IPlaybackError {
        return this.mError;
    }

    public setSessionPlaybackError(error: IPlaybackError): void {
        this.mError = error;
    }

    public getSessionInfo(): Session {
        return this.oSession;
    }
}
